import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useFinding, useFindingAncestry, useFindingContext } from '../api/queries'
import type { Session } from '../api/types'
import { Failure } from '../components/Failure'
import { NetworkContext } from '../components/NetworkContext'
import { ProcessTree } from '../components/ProcessTree'
import { RuleHistory } from '../components/RuleHistory'
import { Severity } from '../components/Severity'

/**
 * One alert's whole story on one page.
 *
 * Ordered the way a person actually reads an alert: what fired and how sure we are,
 * then the evidence in its own words, then the host around it. The remediation is
 * near the top because it is what somebody at 03:00 is looking for, and the integrity
 * hash is at the bottom because it matters enormously and rarely.
 */
export function InvestigationPage({ session }: { session: Session }) {
  const { id } = useParams()
  const query = useFinding(id)
  const [minutes, setMinutes] = useState(30)
  const context = useFindingContext(id, minutes)
  const ancestry = useFindingAncestry(id)

  if (query.isLoading) return <p className="empty">Loading…</p>
  if (query.isError) return <Failure error={query.error} onRetry={() => query.refetch()} />
  if (!query.data) return <p className="empty">No such alert.</p>

  const finding = query.data
  const info = finding.ocsf.finding_info
  const factors = finding.ocsf.severity_factors ?? {}

  return (
    <article aria-labelledby="alert-title">
      <Link to="/queue">← back to the queue</Link>
      <header style={{ margin: 'var(--space-3) 0 var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <Severity id={finding.severity_id} />
          <h2 id="alert-title" style={{ margin: 0 }}>
            {finding.rule_title}
          </h2>
        </div>
        <p className="muted">{info?.desc}</p>
        <p className="mono muted">
          {finding.hostname} · {new Date(finding.first_seen).toLocaleString()} ·{' '}
          {finding.occurrence_count} occurrence(s) · rule {finding.rule_id}
        </p>
      </header>

      <div className="investigation">
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <section className="panel" aria-labelledby="what-to-do">
            <h3 id="what-to-do" style={{ marginTop: 0 }}>
              What to do
            </h3>
            <p>{finding.ocsf.remediation?.desc ?? 'No remediation guidance was recorded.'}</p>
            {finding.ocsf.remediation?.references?.map((reference) => (
              <a key={reference} href={reference} rel="noreferrer noopener" target="_blank">
                {reference}
              </a>
            ))}
          </section>

          <section className="panel" aria-labelledby="evidence">
            <h3 id="evidence" style={{ marginTop: 0 }}>
              Evidence
            </h3>
            {(finding.ocsf.evidences ?? []).map((evidence) => (
              <div
                key={evidence.uid}
                style={{
                  borderBottom: '1px solid var(--border)',
                  paddingBottom: 'var(--space-3)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                <p className="mono muted" style={{ margin: 0 }}>
                  {new Date(evidence.time).toLocaleString()} · {evidence.hostname}
                </p>
                {/* The log line as it arrived. An analyst deciding whether a detection
                    is real asks to see it, every time. */}
                {/* `wrap-anywhere` because this is the one place an 8 KB auditd line
                    arrives verbatim, and it contains no spaces to break at. */}
                <pre
                  className="mono wrap-anywhere"
                  style={{ whiteSpace: 'pre-wrap', margin: 'var(--space-2) 0' }}
                >
                  {evidence.raw_data}
                </pre>
                {Object.entries(evidence.matched_fields ?? {}).length > 0 && (
                  <p className="mono" style={{ margin: 0 }}>
                    matched:{' '}
                    {Object.entries(evidence.matched_fields).map(([field, value]) => (
                      <span key={field} className="badge" style={{ marginRight: 4 }}>
                        {field}={String(value)}
                      </span>
                    ))}
                  </p>
                )}
              </div>
            ))}
          </section>

          <section className="panel" aria-labelledby="ancestry">
            <h3 id="ancestry" style={{ marginTop: 0 }}>
              What spawned this
            </h3>
            {ancestry.isLoading && <p className="muted">Walking the process tree…</p>}
            {/* The tree failing is reported here and nowhere else. It is context, and
                an alert whose story disappears because one panel could not load is an
                alert nobody can work. */}
            {ancestry.isError && (
              <Failure error={ancestry.error} onRetry={() => ancestry.refetch()} />
            )}
            {ancestry.isSuccess && (
              <ProcessTree
                nodes={ancestry.data.nodes}
                applicable={ancestry.data.applicable}
                complete={ancestry.data.complete}
                unobservedParents={ancestry.data.unobserved_parents}
                depthLimited={ancestry.data.depth_limited}
                rowLimited={ancestry.data.row_limited}
              />
            )}
          </section>

          <section className="panel" aria-labelledby="around">
            <h3 id="around" style={{ marginTop: 0 }}>
              On this host, around then
            </h3>
            <label htmlFor="window" className="muted">
              Window (minutes either side){' '}
            </label>
            <select
              id="window"
              value={minutes}
              onChange={(event) => setMinutes(Number(event.target.value))}
            >
              {[5, 30, 120].map((value) => (
                <option key={value} value={value}>
                  ±{value}
                </option>
              ))}
            </select>

            {context.isError && <Failure error={context.error} onRetry={() => context.refetch()} />}
            {context.isSuccess && context.data.results.length === 0 && (
              <p className="empty">No other activity in this window.</p>
            )}
            {context.isSuccess && (
              <ul style={{ listStyle: 'none', padding: 0, maxHeight: 320, overflow: 'auto' }}>
                {context.data.results.map((row, index) => (
                  <li
                    key={String(row.event_id ?? index)}
                    className="mono wrap-anywhere"
                    style={{
                      padding: 'var(--space-1) var(--space-2)',
                      // The alert's own events are marked, not filtered out: the point
                      // of the pivot is to see them in context.
                      borderLeft: row.contributed
                        ? '3px solid var(--sev-high)'
                        : '3px solid transparent',
                      color: row.contributed ? 'var(--text)' : 'var(--text-muted)',
                    }}
                  >
                    {/* Marked with a word as well as a colour: an analyst who cannot
                        distinguish the border colour still needs to know which of
                        these lines the alert was actually built from. */}
                    {row.contributed && (
                      <span className="badge sev-4" style={{ marginRight: 'var(--space-2)' }}>
                        this alert
                      </span>
                    )}
                    {String(row.time ?? '')} {String(row.raw_data ?? '')}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside style={{ display: 'grid', gap: 'var(--space-4)', alignContent: 'start' }}>
          <section className="panel" aria-labelledby="why">
            <h3 id="why" style={{ marginTop: 0 }}>
              Why this severity
            </h3>
            <p className="muted" style={{ marginTop: 0 }}>
              Risk {finding.risk_score} · confidence {finding.confidence_score}%
            </p>
            <ul className="mono" style={{ paddingLeft: 'var(--space-4)' }}>
              {Object.entries(factors).map(([name, value]) => (
                <li key={name}>
                  {name}: {String(value)}
                </li>
              ))}
            </ul>
          </section>

          <section className="panel" aria-labelledby="network">
            <h3 id="network" style={{ marginTop: 0 }}>
              Network around it
            </h3>
            <NetworkContext id={finding.id} />
          </section>

          {/* Directly under "why this severity", because it is the other half of the
              same question: the score says how bad this would be, the history says how
              often it has turned out to be anything at all. */}
          <section className="panel" aria-labelledby="history">
            <h3 id="history" style={{ marginTop: 0 }}>
              This rule before
            </h3>
            <RuleHistory ruleId={finding.rule_id} />
          </section>

          <section className="panel" aria-labelledby="attack">
            <h3 id="attack" style={{ marginTop: 0 }}>
              ATT&amp;CK
            </h3>
            {finding.attack_techniques.length === 0 ? (
              <p className="muted">This rule is not mapped to a technique.</p>
            ) : (
              finding.attack_techniques.map((technique) => (
                <a
                  key={technique}
                  className="badge"
                  style={{ marginRight: 4 }}
                  href={`https://attack.mitre.org/techniques/${technique.replace('.', '/')}/`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {technique}
                </a>
              ))
            )}
          </section>

          <section className="panel" aria-labelledby="asset">
            <h3 id="asset" style={{ marginTop: 0 }}>
              Asset
            </h3>
            {(finding.ocsf.resources ?? []).map((resource) => (
              <p key={resource.uid} className="mono" style={{ margin: 0 }}>
                {resource.name} · criticality {resource.criticality}
                {resource.labels?.length ? ` · ${resource.labels.join(', ')}` : ''}
              </p>
            ))}
          </section>

          <section className="panel" aria-labelledby="integrity">
            <h3 id="integrity" style={{ marginTop: 0 }}>
              Integrity
            </h3>
            <p className="muted" style={{ marginTop: 0 }}>
              Record {finding.sequence_no} in this tenant's chain. Anyone can recompute this.
            </p>
            <p className="mono" style={{ wordBreak: 'break-all', fontSize: 11 }}>
              {finding.row_hash}
            </p>
          </section>

          {!session.memberships.some((membership) => membership.can_triage) && (
            <p className="muted">Your account can read this but not act on it.</p>
          )}
        </aside>
      </div>
    </article>
  )
}
